import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, KeyRound, ShieldCheck, UserCheck } from "lucide-react";

// If PUBLIC_SIGNUP is enabled, users can register without an invite code
const PUBLIC_SIGNUP = process.env.REACT_APP_PUBLIC_SIGNUP === "true";

export default function LoginPage({ onAuth }) {
  // Detect invite code in URL — auto-switch to register mode
  const urlParams = new URLSearchParams(window.location.search);
  const urlInviteCode = urlParams.get("invite") || "";
  const urlRegister = urlParams.get("register") === "1";
  // A link from a reset email: /reset-password?token=...
  const urlResetToken =
    window.location.pathname.replace(/\/+$/, "") === "/reset-password" ? urlParams.get("token") || "" : "";

  const [mode, setMode] = useState(
    urlResetToken ? "reset" : urlInviteCode || urlRegister ? "register" : "login",
  );
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signOutEverywhere, setSignOutEverywhere] = useState(true);
  // Whether reset links go by email. Until email is set up, a forgotten
  // password is passed to an administrator instead, and the form says so.
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [notice, setNotice] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState(urlInviteCode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Clean the invite code from the URL without a page reload
  useEffect(() => {
    if (urlInviteCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [urlInviteCode]);

  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  useEffect(() => {
    if (mode !== "forgot") return;
    fetch(`${API}/auth/password-help`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setEmailEnabled(Boolean(data?.email_enabled)))
      .catch(() => setEmailEnabled(false));
  }, [mode, API]);
  const isClientInvite = Boolean(urlInviteCode);
  // Only show invite code field when arriving via a client invite link
  const showInviteField = mode === "register" && isClientInvite;

  async function handleForgot(e) {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Something went wrong");
        return;
      }
      setNotice(data.message);
    } catch (err) {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("The two passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: urlResetToken, password, sign_out_everywhere: signOutEverywhere }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Something went wrong");
        return;
      }
      // Signed straight in; the token leaves the address bar.
      window.history.replaceState({}, document.title, "/");
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_email", data.email);
      onAuth(data.token, data.email);
    } catch (err) {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
    const body =
      mode === "login"
        ? { email, password }
        : { email, password, invite_code: inviteCode };

    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Something went wrong");
        return;
      }

      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_email", data.email);
      onAuth(data.token, data.email);
    } catch (err) {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#323c44]" data-testid="login-page">
      <Card className="w-full max-w-md border-0 bg-white shadow-xl">
        <CardHeader className="space-y-1 pb-4 pt-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#50e3c2]/10">
            {isClientInvite && mode === "register"
              ? <UserCheck className="h-6 w-6 text-[#119996]" />
              : <ShieldCheck className="h-6 w-6 text-[#119996]" />
            }
          </div>
          <CardTitle className="text-xl font-semibold text-[#323c44]">
            {mode === "forgot"
              ? "Reset your password"
              : mode === "reset"
              ? "Choose a new password"
              : mode === "login"
              ? "Sign in"
              : isClientInvite
              ? "Create your client account"
              : "Create account"}
          </CardTitle>
          <p className="text-sm text-[#4a5b68]">
            {mode === "forgot"
              ? emailEnabled
                ? "Enter your email and we'll send you a link to choose a new one"
                : "Enter your email and we'll let an administrator know, so they can reset it for you"
              : mode === "reset"
              ? "At least 8 characters. You'll be signed in straight away."
              : mode === "login"
              ? "Access your MRR reporting dashboard"
              : isClientInvite
              ? "You've been invited to access a PortalIQ workspace"
              : "Create your account to get started"}
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          {/* Client invite banner */}
          {isClientInvite && mode === "register" && (
            <div className="mb-4 flex items-start gap-2 rounded-lg bg-[#50e3c2]/10 px-3 py-2.5">
              <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#119996]" />
              <p className="text-sm text-[#323c44]">
                Your invite code has been pre-filled. Set a password to complete your account.
              </p>
            </div>
          )}

          {mode === "forgot" && (
            <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-[#323c44]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="border-[#cccccc] pl-10 text-sm focus:border-[#57c1a9] focus:ring-[#57c1a9]"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {notice && (
                <p className="rounded-lg bg-[#e8faf5] px-3 py-2 text-sm text-[#1f5d74]" data-testid="forgot-notice">
                  {notice}
                </p>
              )}
              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading || Boolean(notice)}
                className="w-full bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9]"
                data-testid="forgot-submit-button"
              >
                {loading ? "Please wait..." : emailEnabled ? "Send reset link" : "Ask for help"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode("login"); setError(""); setNotice(""); }}
                  className="text-sm text-[#119996] hover:underline"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={handleReset} className="space-y-4" data-testid="reset-form">
              {[
                ["reset-password", "New password", password, setPassword],
                ["reset-confirm", "Confirm new password", confirmPassword, setConfirmPassword],
              ].map(([id, label, value, setValue]) => (
                <div key={id} className="space-y-2">
                  <Label htmlFor={id} className="text-sm font-medium text-[#323c44]">{label}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
                    <Input
                      id={id}
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      className="border-[#cccccc] pl-10 text-sm focus:border-[#57c1a9] focus:ring-[#57c1a9]"
                      data-testid={`${id}-input`}
                    />
                  </div>
                </div>
              ))}
              <label className="flex items-start gap-2 text-sm text-[#323c44]">
                <input
                  type="checkbox"
                  checked={signOutEverywhere}
                  onChange={(e) => setSignOutEverywhere(e.target.checked)}
                  className="mt-0.5 accent-[#57c1a9]"
                />
                <span>
                  Sign out of all other devices
                  <span className="block text-[11px] text-[#7d8b96]">Recommended if you think someone else knows your password.</span>
                </span>
              </label>
              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="reset-error">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9]"
                data-testid="reset-submit-button"
              >
                {loading ? "Please wait..." : "Set new password"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    window.history.replaceState({}, document.title, "/");
                    setMode("forgot");
                    setError("");
                  }}
                  className="text-sm text-[#119996] hover:underline"
                >
                  Link not working? Request a new one
                </button>
              </div>
            </form>
          )}

          {(mode === "login" || mode === "register") && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#323c44]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="border-[#cccccc] pl-10 text-sm focus:border-[#57c1a9] focus:ring-[#57c1a9]"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#323c44]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Min 8 characters" : ""}
                  className="border-[#cccccc] pl-10 text-sm focus:border-[#57c1a9] focus:ring-[#57c1a9]"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {showInviteField && (
              <div className="space-y-2">
                <Label htmlFor="invite" className="text-sm font-medium text-[#323c44]">Invite code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7d8b96]" />
                  <Input
                    id="invite"
                    type="text"
                    required
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="Paste your invite code"
                    readOnly={Boolean(urlInviteCode)}
                    className={`border-[#cccccc] pl-10 text-sm focus:border-[#57c1a9] focus:ring-[#57c1a9] ${urlInviteCode ? "bg-[#f5f5f5] text-[#4a5b68]" : ""}`}
                    data-testid="login-invite-input"
                  />
                </div>
                {urlInviteCode && (
                  <p className="text-xs text-[#7d8b96]">Code from your invite link — don't change this.</p>
                )}
              </div>
            )}

            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9]"
              data-testid="login-submit-button"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                  className="text-sm text-[#119996] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgotten your password?
                </button>
              </div>
            )}
          </form>
          )}

          {(mode === "login" || mode === "register") && (
          <div className="mt-5 text-center">
            {/* Don't offer "switch to login" when arriving from a fresh invite link */}
            {!(isClientInvite && mode === "register") && (
              <button
                type="button"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
                className="text-sm text-[#119996] hover:underline"
                data-testid="login-toggle-mode"
              >
                {mode === "login" ? "Need an account? Register" : "Already registered? Sign in"}
              </button>
            )}
            {isClientInvite && mode === "register" && (
              <button
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                className="text-sm text-[#119996] hover:underline"
                data-testid="login-toggle-mode"
              >
                Already have an account? Sign in
              </button>
            )}
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
